import React, { useState } from "react";
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Stack,
  Popover,
  IconButton,
} from "@mui/material";
import { Star } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { useTheme, alpha } from "@mui/material/styles";
import {
  selectSubscription,
  selectSubscriptionLoaded,
} from "store/SubscriptionSlice";

// Simple badge component to show upgrade prompt for free users in header
const UpgradeBadge = () => {
  const subscription = useSelector(selectSubscription);
  const subscriptionLoaded = useSelector(selectSubscriptionLoaded);
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  
  // Determine badge visibility and content
  // - lifetime: hide (no countdown)
  // - term: show "X days remaining" when endAt is in future
  // - free (trial): show trial days left or "Trial Ended"
  // - Stripe subscription: hide
  const kind = subscription?.kind;
  const isFreeUser = !subscription || subscription?.tier === "free";
  const isLifetime = kind === "lifetime";
  const isTerm = kind === "term";
  const termDaysLeft = subscription?.term_days_left ?? 0;
  const trialDaysLeft = subscription?.trial_days_left || 0;

  // Show loading indicator if subscription hasn't loaded yet
  if (!subscriptionLoaded) {
    return <CircularProgress size={16} />;
  }

  // Hide badge for lifetime (no countdown)
  if (isLifetime) {
    return null;
  }

  // Term: show days remaining when active, hide when expired
  if (isTerm) {
    if (termDaysLeft <= 0) return null;
  } else if (!isFreeUser) {
    // Hide badge for paid Stripe subscriptions (not free tier)
    return null;
  } else if (trialDaysLeft === 0 && !subscription?.trial_mode) {
    // Free tier but trial ended - still show "Trial Ended"
  }

  // Label: term days remaining or trial days
  const isTermBadge = isTerm && termDaysLeft > 0;
  const daysLeft = isTermBadge ? termDaysLeft : trialDaysLeft;
  const label = daysLeft === 0 
    ? "Trial Ended" 
    : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`;

  const handleOpen = () => {
    setOpen(true);
    setPopoverAnchor(null); // Close popover when opening modal
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleUpgrade = () => {
    setOpen(false);
    window.location.href = "/subscription";
  };

  const handlePopoverOpen = (event) => {
    setPopoverAnchor(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
  };

  const popoverOpen = Boolean(popoverAnchor);

  // Format end date (trial or term)
  const formatEndDate = () => {
    const dateStr = isTermBadge ? subscription?.term_end_at : subscription?.trial_ends_at;
    if (!dateStr) return "N/A";
    
    return new Date(dateStr)
      .toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
      .replace(/(\d{1,2})(,)/, (match, day, comma) => {
        const j = day % 10,
          k = day % 100;
        let suffix = "th";
        if (j == 1 && k != 11) suffix = "st";
        else if (j == 2 && k != 12) suffix = "nd";
        else if (j == 3 && k != 13) suffix = "rd";
        return `${day}${suffix}${comma}`;
      });
  };

  return (
    <>
      <Box 
        sx={{ 
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          onClick={handleOpen}
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "48px",
            padding: "16px",
            borderRadius: "16px",
            cursor: "pointer",
            transition: "background-color .2s ease",
            ...(theme.palette.mode === "dark"
              ? {
                  backgroundColor: alpha(theme.palette.common.white, 0.08),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.35),
                  },
                }
              : {}),
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              color: "#138cef",
              whiteSpace: "nowrap",
              lineHeight: 1,
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
      <Popover
        open={popoverOpen}
        anchorEl={popoverAnchor}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
        sx={{
          pointerEvents: "none",
        }}
        PaperProps={{
          onMouseEnter: handlePopoverOpen,
          onMouseLeave: handlePopoverClose,
          sx: {
            pointerEvents: "auto",
            mt: 1,
            p: 1.5,
            maxWidth: 240,
          },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {daysLeft === 0
            ? "Trial Ended"
            : isTermBadge
              ? `Term: ${daysLeft} day${daysLeft === 1 ? "" : "s"} left`
              : `Trial: ${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {daysLeft === 0
            ? "Your trial has ended. Click to upgrade."
            : isTermBadge
              ? "Click to view your subscription details."
              : "Click to view details and upgrade."}
        </Typography>
      </Popover>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="upgrade-trial-dialog-title"
      >
        <DialogTitle id="upgrade-trial-dialog-title" sx={{ pr: 7 }}>
          {daysLeft === 0
            ? "Your Trial Has Ended"
            : isTermBadge
              ? "Term Access"
              : "Your Trial Ends Soon"}
        </DialogTitle>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
                {daysLeft === 0
                  ? "Your trial period has ended"
                  : `You have ${daysLeft} day${daysLeft === 1 ? "" : "s"} remaining`}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {daysLeft === 0
                  ? "Your trial ended on "
                  : isTermBadge
                    ? "Your access ends on "
                    : "Your trial ends on "}
                <strong>{formatEndDate()}</strong>
              </Typography>
            </Box>

            {!isTermBadge && (
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.action.hover
                      : theme.palette.grey[50],
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Star
                    sx={{
                      color: theme.palette.warning.main,
                      fontSize: 20,
                      mt: 0.25,
                    }}
                  />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Keep all premium features
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Subscribe to keep access to all features and continue using Fractional.Tools.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleUpgrade}
            variant="contained"
          >
            {isTermBadge ? "View Subscription" : "Choose a Plan"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UpgradeBadge;
